import { useEffect, useState } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Users, Plus, Trash, CheckCircle, XCircle } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

const ROLES = [
  { value: "vendedor", label: "Vendedor" },
  { value: "hedge", label: "Hedge" },
  { value: "gestao", label: "Gestão" },
  { value: "admin", label: "Admin" },
];

export default function UsuariosAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ email: "", nome: "", role: "vendedor", limite_usd: 200000 });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateUser = async (id, patch) => {
    try {
      await api.put(`/users/${id}`, patch);
      toast.success("Atualizado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`Excluir usuário ${email}?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Usuário removido");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...form, limite_usd: Number(form.limite_usd) });
      toast.success("Usuário criado");
      setShowNew(false);
      setForm({ email: "", nome: "", role: "vendedor", limite_usd: 200000 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const roleBadgeColor = {
    admin: "bg-[#40B6A2]/10 text-[#40B6A2] border-[#40B6A2]/30",
    hedge: "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30",
    gestao: "bg-purple-500/10 text-purple-400 border-purple-500/30",
    vendedor: "bg-[#5b9bd5]/10 text-[#7ab3e6] border-[#5b9bd5]/30",
  };

  const fmt = (n) => n?.toLocaleString("pt-BR", { minimumFractionDigits: 0 });

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6 max-w-6xl">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[11px] tracking-[0.25em] text-[#6b89a8] uppercase">Administrativo</p>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Users weight="duotone" className="w-8 h-8 text-[#40B6A2]" />
              Gerenciar Usuários
            </h1>
          </div>
          <Button onClick={() => setShowNew(!showNew)} data-testid="btn-toggle-novo" className="bg-[#40B6A2] text-[#061729] hover:bg-[#359a89] font-semibold">
            <Plus weight="bold" className="w-4 h-4 mr-2" /> Novo usuário
          </Button>
        </div>

        {showNew && (
          <form onSubmit={createUser} className="border border-[#40B6A2]/30 bg-[#40B6A2]/5 rounded-sm p-5 space-y-4">
            <h2 className="text-sm tracking-[0.2em] uppercase text-[#40B6A2]">Novo usuário</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#8aa9c9]">Nome</Label>
                <Input data-testid="input-new-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required className="bg-transparent border-[#123a5e] mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#8aa9c9]">E-mail</Label>
                <Input data-testid="input-new-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="bg-transparent border-[#123a5e] mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#8aa9c9]">Perfil</Label>
                <select data-testid="select-new-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full mt-2 bg-transparent border border-[#123a5e] rounded-sm px-3 py-2 text-sm h-10">
                  {ROLES.map((r) => <option key={r.value} value={r.value} className="bg-[#0B2D4D]">{r.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#8aa9c9]">Limite USD</Label>
                <Input data-testid="input-new-limite" type="number" value={form.limite_usd} onChange={(e) => setForm({ ...form, limite_usd: e.target.value })} className="bg-transparent border-[#123a5e] font-mono-data mt-2" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowNew(false)} className="border-[#123a5e]">Cancelar</Button>
              <Button type="submit" data-testid="btn-criar-usuario" className="bg-[#40B6A2] text-[#061729] hover:bg-[#359a89]">Criar</Button>
            </div>
          </form>
        )}

        <div className="border border-[#123a5e] rounded-sm overflow-hidden">
          <div className="p-4 border-b border-[#123a5e] bg-[#0B2D4D] flex items-center justify-between">
            <h2 className="text-sm tracking-[0.2em] uppercase text-[#8aa9c9]">Usuários cadastrados</h2>
            <span className="text-xs text-[#6b89a8] font-mono-data">{users.length} total</span>
          </div>
          {loading ? (
            <div className="p-10 text-center text-[#6b89a8] text-sm">Carregando...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#061729] border-b border-[#123a5e]">
                  <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#6b89a8]">
                    <th className="p-3">Nome</th>
                    <th className="p-3">E-mail</th>
                    <th className="p-3">Perfil</th>
                    <th className="p-3 text-right">Limite USD</th>
                    <th className="p-3">Origem</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`row-user-${u.id}`} className="border-b border-[#0e2e4e] hover:bg-[#0B2D4D]/50">
                      <td className="p-3 font-medium">{u.nome}</td>
                      <td className="p-3 text-xs font-mono-data text-[#b8cde2]">{u.email}</td>
                      <td className="p-3">
                        <select
                          value={u.role}
                          onChange={(e) => updateUser(u.id, { role: e.target.value })}
                          disabled={u.id === me?.id}
                          data-testid={`select-role-${u.id}`}
                          className={`text-xs uppercase tracking-wider px-2 py-1 border rounded-sm bg-transparent font-semibold ${roleBadgeColor[u.role]}`}
                        >
                          {ROLES.map((r) => <option key={r.value} value={r.value} className="bg-[#0B2D4D] text-white">{r.label}</option>)}
                        </select>
                      </td>
                      <td className="p-3 text-right">
                        <Input
                          type="number"
                          defaultValue={u.limite_usd}
                          onBlur={(e) => {
                            const v = Number(e.target.value);
                            if (v !== u.limite_usd) updateUser(u.id, { limite_usd: v });
                          }}
                          data-testid={`input-limite-${u.id}`}
                          className="h-7 w-28 text-xs bg-transparent border-[#123a5e] font-mono-data text-right"
                        />
                      </td>
                      <td className="p-3">
                        <Badge className={`border font-mono-data text-[10px] tracking-wider uppercase ${u.ms_object_id ? "bg-blue-500/10 text-blue-400 border-blue-400/30" : u.password_hash ? "bg-purple-500/10 text-purple-400 border-purple-400/30" : "bg-zinc-500/10 text-zinc-400 border-zinc-500/30"}`}>
                          {u.ms_object_id ? "Azure AD" : u.password_hash ? "Local" : "SSO (1º)"}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => updateUser(u.id, { ativo: !u.ativo })}
                          disabled={u.id === me?.id}
                          data-testid={`btn-toggle-ativo-${u.id}`}
                          className={`flex items-center gap-1 text-xs ${u.ativo ? "text-[#40B6A2]" : "text-[#FF3B30]"}`}
                        >
                          {u.ativo ? <><CheckCircle weight="fill" className="w-4 h-4" /> Ativo</> : <><XCircle weight="fill" className="w-4 h-4" /> Inativo</>}
                        </button>
                      </td>
                      <td className="p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteUser(u.id, u.email)}
                          disabled={u.id === me?.id}
                          data-testid={`btn-delete-${u.id}`}
                          className="h-7 px-2 border-[#123a5e] text-[#8aa9c9] hover:text-[#FF3B30] hover:border-[#FF3B30]"
                        >
                          <Trash className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
